import { useRef, useEffect, useState, memo } from 'react'
import type { CamposDaCnh } from '@/api/parceiros'
import {
  Button,
  Input,
  Select,
  TreeSelect,
  Typography,
  Spin,
  Alert,
  Tag,
  Divider,
  Form,
  Upload,
  Radio,
  QRCode,
  Modal,
} from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  UploadOutlined,
  CopyOutlined,
  WarningOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { Formik, useFormikContext } from 'formik'
import { useFormularioDeMatriculaNoJivo } from '@/lib/contextoDoJivo'
import * as Yup from 'yup'
import { applyCurrencyMask } from '@/lib/mask'
import type { InputRef } from 'antd'
import type { ComponentProps } from 'react'
import { toast } from 'react-toastify'
import { useAuthStore } from '@/stores/authStore'
import {
  useMatriculaStore,
  chaveDaValidacao,
  CAMPOS_DISPENSADOS_NA_VALIDACAO,
  type TreeNode,
} from '@/stores/matriculaStore'
import { useConfiguracaoStore } from '@/stores/configuracaoStore'
import { BRAZILIAN_STATES, CNH_CATEGORIES, STATES_WITH_MUNICIPIO } from '@/lib/constants'
import { formatCurrency, copyToClipboard, isValidCpf, semAcento } from '@/lib/formatters'
import { getApiErrorMessage } from '@/lib/apiError'
import { FormikInput } from '@/components/FormikInput'
import { MunicipioAutoComplete } from '@/components/MunicipioAutoComplete'
import { useEstaEmbutido } from '@/components/Layout'
import type { Captador, Parceiro } from '@/types'

const { Title, Text } = Typography

// Circular numbered badge for a course slot — antd blue palette, no parentheses.
function SlotBadge({ n }: { n: number }) {
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center"
      style={{
        width: 24,
        height: 24,
        borderRadius: '100%',
        background: '#e6f4ff',
        border: '1px solid #91caff',
        color: '#1677ff',
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {n}
    </span>
  )
}

// A busca é pelo nome do CURSO: o primeiro nível é agrupador, e casá-lo derrubaria a filtragem para
// a categoria inteira. O antd mantém a categoria visível quando um filho casa.
type FiltroDeNo = Extract<
  ComponentProps<typeof TreeSelect>['filterTreeNode'],
  (...args: never[]) => unknown
>
type NoDaArvore = Parameters<FiltroDeNo>[1]

function apenasCursosCasam(digitado: string, node: NoDaArvore): boolean {
  if (!node.selectable) return false
  return semAcento(String(node.title ?? '')).includes(semAcento(digitado))
}

// Drop courses already chosen in other rows from a slot's tree (excludeIds), so no course is
// selectable twice. Empty categories are dropped.
function filterCursosTree(tree: TreeNode[], excludeIds: Set<number>): TreeNode[] {
  return tree
    .map((categoria) => ({
      ...categoria,
      children: categoria.children.filter((curso) => !excludeIds.has(curso.value)),
    }))
    .filter((categoria) => categoria.children.length > 0)
}

interface Vendedor {
  cpfCnpj: string
  uf: string
  nome: string
}

// Um captador vende em nome próprio: o backend resolve preço e splits pelo CNPJ do vendedor e sobe
// sozinho até o pai. Trocar o captador é, para todos os efeitos, trocar de vendedor.
function resolverVendedor(
  parceiro: Parceiro | null,
  captadores: Captador[],
  captadorId: number | null
): Vendedor {
  const captador = captadores.find((item) => item.id === captadorId)
  return {
    cpfCnpj: captador?.cpfCnpj ?? parceiro?.cpfCnpj ?? '',
    uf: captador?.uf ?? parceiro?.uf ?? '',
    nome: captador?.nome ?? parceiro?.nome ?? 'O parceiro',
  }
}

function useVendedor(): Vendedor {
  const parceiro = useAuthStore((s) => s.parceiro)
  const captadores = useMatriculaStore((s) => s.captadores)
  const captadorId = useMatriculaStore((s) => s.captadorId)
  return resolverVendedor(parceiro, captadores, captadorId)
}

function vendedorAtual(): Vendedor {
  const { captadores, captadorId } = useMatriculaStore.getState()
  return resolverVendedor(useAuthStore.getState().parceiro, captadores, captadorId)
}

// The floor for the whole selection, read imperatively (outside React) during submit.
function somaCustoDosCursos(): number {
  const { cursoSelecionado, cursosAdicionais, valoresPorCurso } = useMatriculaStore.getState()
  const ids = [cursoSelecionado, ...cursosAdicionais].filter((id): id is number => id != null)
  return ids.reduce((soma, id) => soma + (valoresPorCurso[id]?.valorNewDriver ?? 0), 0)
}

type EnrollmentValues = {
  cpf: string
  nome: string
  dataNascimento: string
  telefone: string
  email: string
  cnh: string
  categoriaCnh: string
  dataPrimeiraHabilitacao: string
  renach: string
  rg: string
  municipio: string
  ufDestino: string
}

const enrollmentInitialValues: EnrollmentValues = {
  cpf: '',
  nome: '',
  dataNascimento: '',
  telefone: '',
  email: '',
  cnh: '',
  categoriaCnh: '',
  dataPrimeiraHabilitacao: '',
  renach: '',
  rg: '',
  municipio: '',
  ufDestino: '',
}

const enrollmentSchema = Yup.object({
  cpf: Yup.string()
    .required('Obrigatório')
    .test('cpf-valid', 'CPF inválido', (value) => isValidCpf(value ?? '')),
  nome: Yup.string().required('Obrigatório'),
  dataNascimento: Yup.string().required('Obrigatório'),
  telefone: Yup.string().required('Obrigatório'),
  email: Yup.string().required('Obrigatório'),
  ufDestino: Yup.string().required('Obrigatório'),
  municipio: Yup.string().when('ufDestino', {
    is: (uf: string) => STATES_WITH_MUNICIPIO.includes(uf),
    then: (schema) => schema.required('Obrigatório'),
  }),
})

// O flow (campos-formulario) responde, por UF e curso, o que o DETRAN exige para aquela matrícula:
// PE se resolve com CPF e nascimento, SP pede CNH, GO pede ainda município e categoria, MS pede RG.
// É a MESMA lista que o backend usa para recusar a validação, então exigi-la aqui é o que faz a
// regra do estado aparecer no formulário em vez de voltar como erro depois da consulta.
function enrollmentSchemaPara(camposDaUf: string[]) {
  const exigidosPelaUf = camposDaUf.filter((campo) => campo in enrollmentInitialValues)
  return enrollmentSchema.shape(
    Object.fromEntries(exigidosPelaUf.map((campo) => [campo, Yup.string().required('Obrigatório')]))
  )
}

// ─── Entry component: zero logic, reads like wireframe ───────────────────────

export function Matricula() {
  return (
    <div className="max-w-3xl mx-auto">
      <Title level={3} className="!mb-6">
        Matricular novo aluno
      </Title>
      <CnhUploadSection />
      <SuccessOrForm />
    </div>
  )
}

// ─── Helper components ────────────────────────────────────────────────────────

// Uma matrícula avulsa termina na tela de detalhes, que já mostra cobrança, links e ações. A venda
// de múltiplos cursos continua com tela própria: são N matrículas para UMA cobrança agregada, e
// nenhum detalhe individual sabe mostrá-la.
function SuccessOrForm() {
  const matriculaResult = useMatriculaStore((s) => s.matriculaResult)
  const cobrancaAgregada = useMatriculaStore((s) => s.cobrancaAgregada)
  if (cobrancaAgregada) return <AggregatedSuccessScreen />
  if (matriculaResult) return <RedirecionaParaDetalhes />
  return <EnrollmentForm />
}

function RedirecionaParaDetalhes() {
  const matriculaResult = useMatriculaStore((s) => s.matriculaResult)!
  const resetForm = useMatriculaStore((s) => s.resetForm)
  const estaEmbutido = useEstaEmbutido()
  const navigate = useNavigate()

  // O reset roda na desmontagem, depois da navegação: limpar antes devolveria o formulário vazio
  // por um quadro, e sem limpar ele volta preenchido no "Nova Matrícula" lá do detalhe.
  useEffect(() => {
    const base = estaEmbutido ? '/embutido/detalhes' : '/detalhes'
    navigate(`${base}/${matriculaResult.id}`, { replace: true })
    return resetForm
  }, [])

  return (
    <div className="flex justify-center py-12">
      <Spin size="large" />
    </div>
  )
}

// Sits outside the Formik block, so what it reads off the CNH reaches the form through the store —
// picked up there by CnhFieldsSyncer.
function CnhUploadSection() {
  const cnhFile = useMatriculaStore((s) => s.cnhFile)
  const cnhExtraindo = useMatriculaStore((s) => s.cnhExtraindo)
  const setCnhFile = useMatriculaStore((s) => s.setCnhFile)
  const extrairDadosDaCnh = useMatriculaStore((s) => s.extrairDadosDaCnh)

  return (
    <div className="mb-6 p-4 border border-dashed border-blue-300 rounded-lg bg-blue-50">
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Text strong>Criar matrícula usando CNH</Text>
          <CnhUploadStatus />
        </div>
        <Upload
          accept=".pdf,.jpg,.jpeg,.png"
          showUploadList={false}
          beforeUpload={(file) => {
            setCnhFile(file)
            extrairDadosDaCnh(file)
            return false // handled here; antd must not try to upload it itself
          }}
        >
          <Button icon={<UploadOutlined />} type="dashed" loading={cnhExtraindo}>
            {cnhFile ? 'Trocar arquivo' : 'Selecionar arquivo'}
          </Button>
        </Upload>
        {cnhFile && !cnhExtraindo && (
          <Button size="small" type="text" danger onClick={() => setCnhFile(null)}>
            Remover
          </Button>
        )}
      </div>
    </div>
  )
}

function CnhUploadStatus() {
  const cnhFile = useMatriculaStore((s) => s.cnhFile)
  const cnhExtraindo = useMatriculaStore((s) => s.cnhExtraindo)
  const cnhExtraida = useMatriculaStore((s) => s.cnhExtraida)

  if (cnhExtraindo) {
    return (
      <p className="text-gray-500 text-sm mt-1">
        <Spin indicator={<LoadingOutlined />} size="small" /> Lendo {cnhFile?.name}...
      </p>
    )
  }

  if (!cnhExtraida) {
    return (
      <p className="text-gray-500 text-sm mt-1">
        Faça upload da foto ou PDF da CNH para preencher os campos automaticamente
      </p>
    )
  }

  const camposLidos = Object.keys(cnhExtraida).length

  if (!camposLidos) {
    return (
      <Tag color="orange" icon={<WarningOutlined />} className="!mt-1">
        Não foi possível ler a CNH — preencha os campos manualmente
      </Tag>
    )
  }

  return (
    <Tag color="green" icon={<CheckCircleOutlined />} className="!mt-1">
      {camposLidos === 1 ? '1 campo lido' : `${camposLidos} campos lidos`} da CNH
    </Tag>
  )
}

function EnrollmentForm() {
  const parceiro = useAuthStore((s) => s.parceiro)
  const campos = useMatriculaStore((s) => s.campos)
  const {
    validarSelecao,
    criarMatricula,
    criarMatriculasComCobranca,
    setUfDestino,
    loadCursos,
    loadCaptadores,
    verificarContaDeRecebimento,
  } = useMatriculaStore()
  const fetchConfig = useConfiguracaoStore((s) => s.fetchConfig)

  useEffect(() => {
    fetchConfig()
    loadCaptadores()
    verificarContaDeRecebimento()
    if (parceiro?.uf) {
      setUfDestino(parceiro.uf)
      loadCursos(parceiro.uf)
    }
  }, [])

  async function handleSubmit(values: EnrollmentValues) {
    const { cursoSelecionado, cursosAdicionais, valoresParceiros, valor, modalidadeCobranca } =
      useMatriculaStore.getState()

    if (!cursoSelecionado) {
      toast.error('Selecione um curso.')
      return
    }

    const multi = cursosAdicionais.some((id) => id != null)
    const piso = multi ? somaCustoDosCursos() : (valoresParceiros?.valorNewDriver ?? 0)

    if (modalidadeCobranca === 'link') {
      const { contaDeRecebimento } = useMatriculaStore.getState()
      if (!contaDeRecebimento?.apto) {
        const faltando = contaDeRecebimento?.pendentes.map((p) => p.nome).join(', ')
        toast.error(
          `Conta de recebimento não configurada: ${faltando ?? 'verifique as configurações'}.`
        )
        return
      }
      if (valor < piso) {
        toast.error(`Valor mínimo: ${formatCurrency(piso)}`)
        return
      }
    }

    // Nada é criado antes de o aluno passar em TODOS os cursos escolhidos — um curso que ele não
    // pode fazer neste estado não pode deixar os outros matriculados. Se o parceiro já validou esta
    // mesma seleção pelo botão "Validar", validarSelecao devolve aquele veredito sem repetir a ida
    // ao DETRAN. Os resultados aparecem em ValidationStatus; no multi, N matrículas viram uma única
    // cobrança agregada.
    const vendedor = vendedorAtual()
    if (!vendedor.cpfCnpj) return

    const apto = await validarSelecao(values)
    if (!apto) {
      const { validacaoAluno } = useMatriculaStore.getState()
      toast.error(
        multi
          ? 'O aluno não está apto em um ou mais cursos selecionados.'
          : validacaoAluno.mensagemErro || 'Aluno não apto para matrícula.'
      )
      return
    }

    if (multi) {
      try {
        await criarMatriculasComCobranca(vendedor.cpfCnpj, vendedor.uf, values)
        toast.success('Matrículas criadas! Cobrança agregada gerada abaixo.')
      } catch (err: unknown) {
        toast.error(getApiErrorMessage(err, 'Erro ao criar as matrículas.'))
      }
      return
    }

    try {
      await criarMatricula(vendedor.cpfCnpj, vendedor.uf, values)
      toast.success('Matrícula realizada com sucesso!')
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Erro ao criar matrícula.'))
    }
  }

  const initialValues: EnrollmentValues = {
    ...enrollmentInitialValues,
    ufDestino: parceiro?.uf ?? '',
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={enrollmentSchemaPara(campos)}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit: submitForm }) => (
        <Form layout="vertical" onFinish={() => submitForm()} className="space-y-6">
          <CnhFieldsSyncer />
          <ContextoDoJivoSyncer />
          <ValidacaoSyncer />
          <CourseSection />
          <StudentDataSection />
          <PriceSection />
          <ValidationStatus />
          <SubmitButton />
        </Form>
      )}
    </Formik>
  )
}

// Também não renderiza nada: leva o rascunho do formulário para o painel do atendente do Jivo, e
// para isso precisa estar dentro do <Formik>.
function ContextoDoJivoSyncer() {
  const { values } = useFormikContext<EnrollmentValues>()
  useFormularioDeMatriculaNoJivo(values)
  return null
}

// Também não renderiza nada: o veredito da validação vale para os dados exatos que a consulta usou,
// então editar qualquer um deles — um campo do aluno, um curso — o tira da tela e devolve o botão
// "Validar" ao parceiro.
function ValidacaoSyncer() {
  const chaveAtual = useChaveDaValidacao()
  const validado = useMatriculaStore((s) => s.validacaoSnapshot)
  const limparValidacao = useMatriculaStore((s) => s.limparValidacao)

  useEffect(() => {
    if (validado && validado !== chaveAtual) limparValidacao()
  }, [chaveAtual, validado, limparValidacao])

  return null
}

function useChaveDaValidacao(): string {
  const { values } = useFormikContext<EnrollmentValues>()
  return useMatriculaStore((s) => chaveDaValidacao(s, values))
}

// Renders nothing — it exists to carry what was read off the CNH into the Formik values, which is
// why it must live inside the <Formik> block while the upload control does not.
//
// A CNH for a DIFFERENT CPF than the one on screen means the parceiro moved on to another student,
// so the form is wiped and rebuilt from that document — the same thing editing the CPF by hand does
// in CpfField. A CNH for the SAME person only fills the gaps, because there anything already on
// screen came either from the aluno's record or from a deliberate correction, and both outrank OCR.
function CnhFieldsSyncer() {
  const cnhExtraida = useMatriculaStore((s) => s.cnhExtraida)
  const searchAlunoByCpf = useMatriculaStore((s) => s.searchAlunoByCpf)
  const clearAluno = useMatriculaStore((s) => s.clearAluno)
  const { values, setValues } = useFormikContext<EnrollmentValues>()

  useEffect(() => {
    if (!cnhExtraida) return

    const outroAluno = !!cnhExtraida.cpf && !!values.cpf && cnhExtraida.cpf !== values.cpf
    if (outroAluno) clearAluno() // unlocks the fields the previous aluno's record had frozen

    // Destination state is a property of the sale, not of the student, so it survives the swap.
    const partida = outroAluno
      ? { ...enrollmentInitialValues, ufDestino: values.ufDestino }
      : values

    const aPreencher = camposAindaVazios(cnhExtraida, partida)
    if (!outroAluno && !Object.keys(aPreencher).length) return

    const preenchidos = { ...partida, ...aPreencher }
    setValues(preenchidos)

    // The CNH hands us the CPF, which is the key to an existing student. Looking them up here saves
    // the parceiro from having to click into the field just to fire the blur that normally does it.
    if (preenchidos.cpf && isValidCpf(preenchidos.cpf)) {
      searchAlunoByCpf(preenchidos.cpf, (doRegistro) =>
        setValues({ ...preenchidos, ...doRegistro })
      )
    }
  }, [cnhExtraida])

  return null
}

function camposAindaVazios(
  extraidos: CamposDaCnh,
  values: EnrollmentValues
): Partial<EnrollmentValues> {
  const lidos = Object.entries(extraidos) as [keyof CamposDaCnh, string | undefined][]
  return Object.fromEntries(
    lidos.filter(([campo, valor]) => valor && !values[campo])
  ) as Partial<EnrollmentValues>
}

// A found aluno's fields are locked ONLY where the DB already has a value. Fields the record left
// empty (e.g. a missing dataPrimeiraHabilitacao) stay editable so the parceiro can complete them
// before enrolling. Derived from the loaded record, never the live form value, so a field can't
// re-lock itself the moment the user starts typing into it.
//
// Telefone e e-mail ficam de fora: createMatricula regrava os dois no cadastro do aluno.
function useFieldLock() {
  const aluno = useMatriculaStore((s) => s.alunoExistente)

  return (field: keyof EnrollmentValues): boolean => {
    if (!aluno) return false
    const valorNoRegistro: Partial<Record<keyof EnrollmentValues, unknown>> = {
      nome: aluno.nome,
      dataNascimento: aluno.dataNascimento,
      cnh: aluno.cnh,
      categoriaCnh: aluno.categoriaCnh,
      dataPrimeiraHabilitacao: aluno.dataPrimeiraHabilitacao,
      rg: aluno.rg,
      municipio: aluno.municipio,
    }
    return !!valorNoRegistro[field]
  }
}

function CpfField() {
  const { values, setValues } = useFormikContext<EnrollmentValues>()
  const searchingCpf = useMatriculaStore((s) => s.searchingCpf)
  const alunoExistente = useMatriculaStore((s) => s.alunoExistente)
  const searchAlunoByCpf = useMatriculaStore((s) => s.searchAlunoByCpf)
  const clearAluno = useMatriculaStore((s) => s.clearAluno)

  async function handleBlur() {
    if (!isValidCpf(values.cpf)) return
    await searchAlunoByCpf(values.cpf, (autoFilled) => setValues({ ...values, ...autoFilled }))
  }

  function handleCpfChange(newCpf: string) {
    if (alunoExistente) {
      clearAluno()
      setValues({ ...enrollmentInitialValues, cpf: newCpf, ufDestino: values.ufDestino })
    }
  }

  return (
    <FormikInput
      name="cpf"
      label="CPF *"
      size="large"
      placeholder="000.000.000-00"
      mask="999.999.999-99"
      formItemClassName="md:col-span-2"
      suffix={
        <Spin
          indicator={<LoadingOutlined />}
          size="small"
          style={{ visibility: searchingCpf ? 'visible' : 'hidden' }}
        />
      }
      extra={
        alunoExistente ? (
          <Text type="success">Aluno encontrado — edite o CPF para limpar</Text>
        ) : undefined
      }
      onBlur={handleBlur}
      onChangeValue={handleCpfChange}
    />
  )
}

function DateField({ name, label }: { name: string; label: string }) {
  const isLocked = useFieldLock()

  return (
    <FormikInput
      name={name}
      label={label}
      size="large"
      disabled={isLocked(name as keyof EnrollmentValues)}
      mask="99/99/9999"
      placeholder="DD/MM/AAAA"
    />
  )
}

function CategoriaCnhSelect() {
  const { values, setFieldValue, setFieldTouched, touched, errors } =
    useFormikContext<EnrollmentValues>()
  const isLocked = useFieldLock()

  return (
    <Form.Item
      label="Categoria CNH *"
      validateStatus={touched.categoriaCnh && errors.categoriaCnh ? 'error' : ''}
      help={touched.categoriaCnh && errors.categoriaCnh}
    >
      <Select
        value={values.categoriaCnh || undefined}
        onChange={(v) => setFieldValue('categoriaCnh', v)}
        onBlur={() => setFieldTouched('categoriaCnh', true)}
        disabled={isLocked('categoriaCnh')}
        size="large"
        placeholder="Selecione"
        options={CNH_CATEGORIES.map((c) => ({ label: c, value: c }))}
      />
    </Form.Item>
  )
}

const StudentDataSection = memo(function StudentDataSection() {
  const campos = useMatriculaStore((s) => s.campos)
  const isLocked = useFieldLock()
  const campoSet = new Set(campos)

  // O CPF ocupa a linha inteira; os outros cinco campos fixos ocupam meia coluna cada, e cada campo
  // opcional exibido entra na conta. Ímpar significa última linha pela metade — a lacuna que o botão
  // "Validar" preenche em vez de abrir uma linha só para ele.
  const opcionaisExibidos = ['cnh', 'categoriaCnh', 'dataPrimeiraHabilitacao', 'rg'].filter(
    (campo) => campoSet.has(campo)
  )
  const sobraMeiaColuna = (5 + opcionaisExibidos.length) % 2 === 1

  return (
    <fieldset className="border border-gray-200 rounded-lg p-4 bg-white">
      <legend className="px-2 text-sm font-semibold text-gray-600">Dados do Aluno</legend>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CpfField />
        <FormikInput name="nome" label="Nome *" size="large" disabled={isLocked('nome')} />
        <DateField name="dataNascimento" label="Data de Nascimento *" />
        <FormikInput
          name="telefone"
          label="Telefone *"
          size="large"
          mask="(99) 99999-9999"
          placeholder="(00) 00000-0000"
        />
        <FormikInput name="email" label="E-mail *" size="large" />

        {campoSet.has('cnh') && (
          <FormikInput
            name="cnh"
            label="CNH *"
            size="large"
            disabled={isLocked('cnh')}
            mask="999999999-99"
          />
        )}

        {campoSet.has('categoriaCnh') && <CategoriaCnhSelect />}

        {campoSet.has('dataPrimeiraHabilitacao') && (
          <DateField name="dataPrimeiraHabilitacao" label="Data 1ª Habilitação *" />
        )}

        {campoSet.has('rg') && (
          <FormikInput name="rg" label="RG *" size="large" disabled={isLocked('rg')} />
        )}

        <FormikInput
          name="renach"
          label={campoSet.has('renach') ? 'RENACH *' : 'RENACH'}
          size="large"
        />

        <BotaoValidar preencheLacuna={sobraMeiaColuna} />
      </div>
    </fieldset>
  )
})

// Valida o aluno sem matriculá-lo. O resultado abre num modal e fica na tela (ValidationStatus): é a
// mesma validação que o envio faria, então matricular em seguida não repete a consulta.
function BotaoValidar({ preencheLacuna }: { preencheLacuna: boolean }) {
  const { values, validateForm, setTouched } = useFormikContext<EnrollmentValues>()
  const [modalAberto, setModalAberto] = useState(false)
  const cursoSelecionado = useMatriculaStore((s) => s.cursoSelecionado)
  const validarSelecao = useMatriculaStore((s) => s.validarSelecao)
  const validando = useValidacaoEmAndamento()

  async function handleValidar() {
    const erros = await validateForm()
    const faltando = Object.keys(erros).filter(
      (campo) => !CAMPOS_DISPENSADOS_NA_VALIDACAO.includes(campo)
    )
    if (faltando.length) {
      setTouched(Object.fromEntries(faltando.map((campo) => [campo, true])))
      toast.error('Preencha os campos obrigatórios para validar.')
      return
    }
    if (!cursoSelecionado) {
      toast.error('Selecione um curso.')
      return
    }
    setModalAberto(true)
    await validarSelecao(values)
  }

  return (
    <div className={`flex items-end justify-end ${preencheLacuna ? 'pb-6' : 'md:col-span-2'}`}>
      <Button onClick={handleValidar} loading={validando}>
        Validar
      </Button>
      <Modal
        title="Validação do aluno"
        open={modalAberto}
        onOk={() => setModalAberto(false)}
        onCancel={() => setModalAberto(false)}
        okText="Fechar"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <ValidationStatus />
      </Modal>
    </div>
  )
}

function useValidacaoEmAndamento(): boolean {
  const validacaoAluno = useMatriculaStore((s) => s.validacaoAluno)
  const validacaoPorCurso = useMatriculaStore((s) => s.validacaoPorCurso)
  return validacaoAluno.loading || Object.values(validacaoPorCurso).some((v) => v.loading)
}

function UfDestinoSelect() {
  const { values, setFieldValue, touched, errors } = useFormikContext<EnrollmentValues>()
  const vendedor = useVendedor()
  const { loadCursos, setUfDestino, recalcularParaUf } = useMatriculaStore()

  async function handleUfChange(uf: string) {
    setFieldValue('ufDestino', uf)
    setUfDestino(uf)
    if (uf) {
      await loadCursos(uf) // keeps the selected courses; only refreshes the catalog
      if (vendedor.cpfCnpj) await recalcularParaUf(vendedor.cpfCnpj, vendedor.uf, uf)
    }
  }

  return (
    <Form.Item
      label="UF da matrícula *"
      validateStatus={touched.ufDestino && errors.ufDestino ? 'error' : ''}
      help={touched.ufDestino && errors.ufDestino}
    >
      <Select
        value={values.ufDestino || undefined}
        onChange={handleUfChange}
        size="large"
        placeholder="Selecione o estado"
        showSearch
        options={BRAZILIAN_STATES.map((uf) => ({ label: uf, value: uf }))}
      />
    </Form.Item>
  )
}

const CourseSection = memo(function CourseSection() {
  const ufDestino = useMatriculaStore((s) => s.ufDestino)
  const showMunicipio = STATES_WITH_MUNICIPIO.includes(ufDestino)

  return (
    <fieldset className="border border-gray-200 rounded-lg p-4 bg-white">
      <legend className="px-2 text-sm font-semibold text-gray-600">Curso</legend>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <UfDestinoSelect />

        {showMunicipio && <MunicipioField />}

        <CourseList />
      </div>
    </fieldset>
  )
})

function MunicipioField() {
  const { values, setFieldValue, touched, errors } = useFormikContext<EnrollmentValues>()

  return (
    <MunicipioAutoComplete
      uf={values.ufDestino}
      value={values.municipio}
      onChange={(municipio) => setFieldValue('municipio', municipio)}
      label="Município da matrícula *"
      size="large"
      erro={touched.municipio ? errors.municipio : undefined}
    />
  )
}

// One uniform list of course slots for the same student. The FIRST slot is the primary course
// (drives the form fields + single-course billing); the rest are additionals. Every slot looks the
// same — a (n) badge and a remove button — but only when there is more than one course. With a single
// course, no badge/remove is shown. Each course becomes its own matrícula; multiple are charged via a
// single aggregated PIX.
function CourseList() {
  const vendedor = useVendedor()
  const cursosTree = useMatriculaStore((s) => s.cursosTree)
  const ufDestino = useMatriculaStore((s) => s.ufDestino)
  const cursoSelecionado = useMatriculaStore((s) => s.cursoSelecionado)
  const cursosAdicionais = useMatriculaStore((s) => s.cursosAdicionais)
  const {
    setCurso,
    getValoresParceiros,
    getValoresCursoAdicional,
    getCamposFormulario,
    setCursoAdicional,
    addCursoAdicional,
    removeCursoAdicional,
  } = useMatriculaStore()

  async function selectPrimary(cursoId: number | null) {
    setCurso(cursoId)
    if (cursoId && vendedor.cpfCnpj) {
      await Promise.all([
        getValoresParceiros(vendedor.cpfCnpj, vendedor.uf, ufDestino, cursoId),
        getCamposFormulario(ufDestino, cursoId),
      ])
    }
  }

  async function selectAdditional(index: number, cursoId: number | null) {
    setCursoAdicional(index, cursoId)
    if (cursoId && vendedor.cpfCnpj) {
      await getValoresCursoAdicional(vendedor.cpfCnpj, vendedor.uf, ufDestino, cursoId)
    }
  }

  // Removing the primary promotes the next course into its place (and refetches its values/fields).
  async function removePrimary() {
    const promoted = cursosAdicionais[0] ?? null
    removeCursoAdicional(0)
    await selectPrimary(promoted)
  }

  const slots = [
    { id: cursoSelecionado, onSelect: selectPrimary, onRemove: removePrimary },
    ...cursosAdicionais.map((id, index) => ({
      id,
      onSelect: (value: number | null) => selectAdditional(index, value),
      onRemove: () => removeCursoAdicional(index),
    })),
  ]
  const multi = slots.length > 1
  const selectedIds = new Set(slots.map((slot) => slot.id).filter((id): id is number => id != null))

  return (
    <div className="md:col-span-2 space-y-2">
      <Text strong>{multi ? 'Cursos *' : 'Curso *'}</Text>
      {slots.map((slot, idx) => (
        <div key={idx} className="flex items-center gap-2">
          {multi && <SlotBadge n={idx + 1} />}
          <CursoSlotSelect
            cursoId={slot.id}
            onSelect={slot.onSelect}
            // Exclude courses chosen in OTHER rows, so the same course can't be picked twice.
            arvore={filterCursosTree(
              cursosTree,
              new Set([...selectedIds].filter((id) => id !== slot.id))
            )}
            desabilitado={!ufDestino}
            abrirAoMontar={idx > 0 && idx === slots.length - 1 && slot.id == null}
          />
          {multi && (
            <Button
              danger
              type="text"
              icon={<CloseCircleOutlined />}
              onClick={slot.onRemove}
              aria-label="Remover curso"
            />
          )}
        </div>
      ))}
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={addCursoAdicional}
        disabled={!ufDestino}
      >
        Adicionar outro curso
      </Button>
      {multi && (
        <Alert
          type="info"
          showIcon
          className="!mt-2"
          message="Vários cursos serão cobrados em uma única cobrança agregada (PIX), paga pelo parceiro."
        />
      )}
    </div>
  )
}

/*
 * CurrencyInput — keyboard-intercept approach (commented out, may revisit)
 *
 * Stores raw cents as an integer, derives display string via math (no mask lib).
 * Pros: no external dependency, full control over digit shifting.
 * Cons: cursor is always forced to end; no support for in-place editing.
 *
function CurrencyInput({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [cents, setCents] = useState(Math.round(value * 100))

  useEffect(() => { setCents(Math.round(value * 100)) }, [value])

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const isAllSelected =
      e.currentTarget.selectionStart === 0 &&
      e.currentTarget.selectionEnd === e.currentTarget.value.length

    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault()
      if (isAllSelected) { setCents(0); onChange(0) }
      else if (e.key === 'Backspace') {
        const next = Math.floor(cents / 10)
        setCents(next); onChange(next / 100)
      }
    } else if (/^\d$/.test(e.key)) {
      e.preventDefault()
      const next = cents * 10 + parseInt(e.key, 10)
      if (next > 9_999_999) return
      setCents(next); onChange(next / 100)
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault()
    const digits = e.clipboardData.getData('text').replace(/\D/g, '')
    const next = Math.min(parseInt(digits || '0', 10), 9_999_999)
    setCents(next); onChange(next / 100)
  }

  const integerPart = Math.floor(cents / 100)
  const decimalPart = (cents % 100).toString().padStart(2, '0')
  const integerFormatted = integerPart.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  const formatted = `R$ ${integerFormatted},${decimalPart}`

  return (
    <Input value={formatted} onKeyDown={handleKeyDown} onPaste={handlePaste}
      onChange={() => {}} size="large" style={{ width: 200 }} />
  )
}
*/

/**
 * O antd nunca ativa um item sozinho aqui: ao procurar o primeiro nó ele PULA os não-selecionáveis
 * sem descer nos filhos, e as categorias são exatamente isso. Sem item ativo, Enter não seleciona
 * nada — e o `onInputKeyDown` do TreeSelect não serve para corrigir isso, porque o rc-select o
 * descarta (DEFAULT_OMIT_PROPS) e usa o seu próprio. Daí a captura no wrapper.
 */
function CursoSlotSelect({
  cursoId,
  onSelect,
  arvore,
  desabilitado,
  abrirAoMontar,
}: {
  cursoId: number | null
  onSelect: (cursoId: number | null) => void
  arvore: TreeNode[]
  desabilitado: boolean
  abrirAoMontar: boolean
}) {
  const [busca, setBusca] = useState('')
  const [aberto, setAberto] = useState(abrirAoMontar)

  function selecionar(novoCursoId: number | null) {
    setBusca('')
    setAberto(false)
    onSelect(novoCursoId)
  }

  function handleEnter(evento: React.KeyboardEvent<HTMLDivElement>) {
    if (evento.key !== 'Enter' || !aberto) return
    const primeiro = primeiroCursoDaLista(arvore, busca)
    if (primeiro == null) return
    evento.preventDefault()
    evento.stopPropagation()
    selecionar(primeiro)
  }

  return (
    <div className="flex-1" onKeyDownCapture={handleEnter}>
      <TreeSelect
        value={cursoId ?? undefined}
        onChange={(value) => selecionar((value as number) ?? null)}
        treeData={arvore}
        size="large"
        placeholder="Selecione um curso"
        showSearch
        searchValue={busca}
        onSearch={setBusca}
        open={aberto}
        onOpenChange={setAberto}
        filterTreeNode={apenasCursosCasam}
        treeExpandAction="click"
        treeDefaultExpandAll
        className="curso-tree-select"
        style={{ width: '100%' }}
        disabled={desabilitado}
        listHeight={600}
        autoFocus={abrirAoMontar}
      />
    </div>
  )
}

function primeiroCursoDaLista(arvore: TreeNode[], busca: string): number | null {
  for (const categoria of arvore) {
    for (const curso of categoria.children) {
      if (!busca || semAcento(curso.title).includes(semAcento(busca))) return curso.value
    }
  }
  return null
}

function CurrencyInput({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const inputRef = useRef<InputRef>(null)

  useEffect(() => {
    const el = inputRef.current?.input
    if (!el) return
    applyCurrencyMask(el)
    if (value) el.value = value.toFixed(2).replace('.', ',')
    el.dispatchEvent(new Event('input', { bubbles: true }))
  }, [])

  // Sync when external value changes while mounted (e.g. store reset)
  useEffect(() => {
    const el = inputRef.current?.input
    if (!el) return
    const current = parseFloat(el.value.replace('R$ ', '').replace(/\./g, '').replace(',', '.'))
    if (Math.abs(current - value) > 0.001) {
      el.value = value.toFixed(2).replace('.', ',')
      el.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }, [value])

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value.replace('R$ ', '').replace(/\./g, '').replace(',', '.')
    const num = parseFloat(raw)
    onChange(isNaN(num) ? 0 : num)
  }

  return (
    <Input
      ref={inputRef}
      onChange={handleChange}
      size="large"
      style={{ textAlign: 'right', fontSize: 18, fontWeight: 600 }}
    />
  )
}

function PriceSection() {
  const valoresParceiros = useMatriculaStore((s) => s.valoresParceiros)
  const modalidadeCobranca = useMatriculaStore((s) => s.modalidadeCobranca)
  const cursosAdicionais = useMatriculaStore((s) => s.cursosAdicionais)
  const cursosSemPreco = useCursosSemPreco()
  const multi = cursosAdicionais.some((id) => id != null)

  if (cursosSemPreco.length) return <PrecoNaoConfiguradoAlert cursos={cursosSemPreco} />
  if (!valoresParceiros) return null

  // Multi-course keeps the same billing UI — the charge is one aggregated cobrança (single
  // pagamentoId) the student can pay by link, credit card or PIX — plus a per-course breakdown so the
  // New Driver total is transparent. The billing's New Driver floor is the SUM across all courses.
  return (
    <fieldset className="border border-gray-200 rounded-lg p-4 bg-white">
      <legend className="px-2 text-sm font-semibold text-gray-600">Cobrança</legend>
      <div className="space-y-4">
        {multi && <CoursePriceBreakdown />}
        <CaptadorSelect />
        <p>Quem efetuará o pagamento?</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <BillingModeCard
            value="manual"
            title={<NomeDoVendedor />}
            description="Você gerencia a cobrança com o aluno. New Driver emite uma cobrança para o parceiro."
          />
          <BillingModeCard
            value="link"
            title={<NomeDoAluno />}
            description="O aluno paga pelo link. Você recebe seu repasse automaticamente ao confirmar o pagamento."
          />
        </div>

        {modalidadeCobranca === 'manual' && <ManualBillingInfo />}
        {modalidadeCobranca === 'link' && <LinkBillingContent />}
      </div>
    </fieldset>
  )
}

function CaptadorSelect() {
  const captadores = useMatriculaStore((s) => s.captadores)
  const captadorId = useMatriculaStore((s) => s.captadorId)
  const setCaptador = useMatriculaStore((s) => s.setCaptador)
  const ufDestino = useMatriculaStore((s) => s.ufDestino)
  const recalcularParaUf = useMatriculaStore((s) => s.recalcularParaUf)
  const verificarContaDeRecebimento = useMatriculaStore((s) => s.verificarContaDeRecebimento)

  const ativos = captadores.filter((captador) => !captador.inativadoEm)
  if (!ativos.length) return null

  async function handleChange(novoCaptadorId: number | null) {
    setCaptador(novoCaptadorId ?? null)
    await verificarContaDeRecebimento()
    const vendedor = vendedorAtual()
    if (vendedor.cpfCnpj && ufDestino) {
      await recalcularParaUf(vendedor.cpfCnpj, vendedor.uf, ufDestino)
    }
  }

  return (
    <Form.Item
      label={<Text type="secondary">Matriculando para um captador?</Text>}
      layout="horizontal"
    >
      <Select
        value={captadorId ?? undefined}
        onChange={handleChange}
        allowClear
        showSearch
        optionFilterProp="label"
        placeholder="Nenhum — matrícula própria"
        style={{ maxWidth: 360 }}
        options={ativos.map((captador) => ({ label: captador.nome, value: captador.id }))}
      />
    </Form.Item>
  )
}

function NomeDoVendedor() {
  const vendedor = useVendedor()
  return <>{vendedor.nome}</>
}

function NomeDoAluno() {
  const { values } = useFormikContext<EnrollmentValues>()
  return <>{values.nome?.trim() || 'O aluno'}</>
}

// Um curso sem preço configurado responde 200 com valorNewDriver nulo — sem tratar, o piso vira 0.
function useCursosSemPreco(): string[] {
  const cursoSelecionado = useMatriculaStore((s) => s.cursoSelecionado)
  const cursosAdicionais = useMatriculaStore((s) => s.cursosAdicionais)
  const semPrecoPorCurso = useMatriculaStore((s) => s.semPrecoPorCurso)
  const allCursos = useMatriculaStore((s) => s.allCursos)

  const nomePorId = new Map(allCursos.map((curso) => [curso.id, curso.nomeReduzido || curso.nome]))
  return [cursoSelecionado, ...cursosAdicionais]
    .filter((id): id is number => id != null && semPrecoPorCurso[id])
    .map((id) => nomePorId.get(id) ?? `Curso ${id}`)
}

function PrecoNaoConfiguradoAlert({ cursos }: { cursos: string[] }) {
  const vendedor = useVendedor()

  return (
    <Alert
      type="warning"
      showIcon
      icon={<WarningOutlined />}
      message="Curso sem preço configurado"
      description={`Ainda não há preço configurado para ${cursos.join(', ')} nesta UF em nome de ${vendedor.nome}. Peça a configuração antes de matricular.`}
    />
  )
}

// The New Driver floor for the current selection: the single course's cost, or the sum across all
// selected courses when there is more than one.
function useValorNewDriverTotal(): number {
  const cursoSelecionado = useMatriculaStore((s) => s.cursoSelecionado)
  const cursosAdicionais = useMatriculaStore((s) => s.cursosAdicionais)
  const valoresParceiros = useMatriculaStore((s) => s.valoresParceiros)
  const valoresPorCurso = useMatriculaStore((s) => s.valoresPorCurso)

  if (!cursosAdicionais.some((id) => id != null)) return valoresParceiros?.valorNewDriver ?? 0
  const ids = [cursoSelecionado, ...cursosAdicionais].filter((id): id is number => id != null)
  return ids.reduce((soma, id) => soma + (valoresPorCurso[id]?.valorNewDriver ?? 0), 0)
}

// Per-course breakdown for a multi-course sale: what each course costs the parceiro, summing to the
// floor. It itemizes COURSES, not the parties being paid — the payout chain stays hidden.
function CoursePriceBreakdown() {
  const cursoSelecionado = useMatriculaStore((s) => s.cursoSelecionado)
  const cursosAdicionais = useMatriculaStore((s) => s.cursosAdicionais)
  const valoresPorCurso = useMatriculaStore((s) => s.valoresPorCurso)
  const allCursos = useMatriculaStore((s) => s.allCursos)

  const nomePorId = new Map(allCursos.map((curso) => [curso.id, curso.nomeReduzido || curso.nome]))
  const ids = [cursoSelecionado, ...cursosAdicionais].filter((id): id is number => id != null)
  const total = ids.reduce((soma, id) => soma + (valoresPorCurso[id]?.valorNewDriver ?? 0), 0)

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <Text strong className="!mb-2 block">
        Cursos ({ids.length})
      </Text>
      <div className="space-y-1">
        {ids.map((id, idx) => (
          <div key={id} className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2">
              <SlotBadge n={idx + 1} />
              {nomePorId.get(id) ?? `Curso ${id}`}
            </span>
            <span>
              {valoresPorCurso[id] ? formatCurrency(valoresPorCurso[id].valorNewDriver) : '—'}
            </span>
          </div>
        ))}
      </div>
      <Divider className="!my-2" />
      <div className="flex items-center justify-between">
        <Text type="secondary">Custo total</Text>
        <Text strong>{formatCurrency(total)}</Text>
      </div>
    </div>
  )
}

function BillingModeCard({
  value,
  title,
  description,
}: {
  value: 'link' | 'manual'
  title: React.ReactNode
  description: string
}) {
  const modalidadeCobranca = useMatriculaStore((s) => s.modalidadeCobranca)
  const setModalidadeCobranca = useMatriculaStore((s) => s.setModalidadeCobranca)
  const selected = modalidadeCobranca === value

  return (
    <label
      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
        selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <Radio checked={selected} onChange={() => setModalidadeCobranca(value)} className="mt-0.5" />
      <div>
        <Text strong>{title}</Text>
        <p className="text-sm text-gray-500 mt-0.5 mb-0">{description}</p>
      </div>
    </label>
  )
}

function ManualBillingInfo() {
  const valorNewDriver = useValorNewDriverTotal()
  const vendedor = useVendedor()
  const captadorId = useMatriculaStore((s) => s.captadorId)
  const linhas = useRepasseAgregado()
  const parceiro = useAuthStore((s) => s.parceiro)
  const seuRepasse = linhas.find((linha) => linha.descricao === parceiro?.nome)?.valor ?? 0

  return (
    <Alert
      type="info"
      showIcon
      description={
        <span>
          Uma cobrança de <strong>{formatCurrency(valorNewDriver)}</strong> será gerada para{' '}
          <strong>{vendedor.nome}</strong> pagar à New Driver.
          {captadorId != null && (
            <>
              {' '}
              Desse total, <strong>{formatCurrency(seuRepasse)}</strong> é o seu repasse.
            </>
          )}{' '}
          Quem vende decide como e quanto cobrar do aluno por conta própria.
        </span>
      }
    />
  )
}

function LinkBillingContent() {
  const conta = useMatriculaStore((s) => s.contaDeRecebimento)

  if (!conta) return <Spin size="small" />
  if (!conta.apto) return <ContaDeRecebimentoPendente pendentes={conta.pendentes} />

  return <LinkBillingForm />
}

function ContaDeRecebimentoPendente({
  pendentes,
}: {
  pendentes: Array<{ id: number; nome: string; ehVoce: boolean }>
}) {
  const voce = pendentes.find((pendente) => pendente.ehVoce)
  const outros = pendentes.filter((pendente) => !pendente.ehVoce)

  return (
    <Alert
      type="warning"
      showIcon
      icon={<WarningOutlined />}
      description={
        <div className="space-y-1">
          {voce && <PendenciaDaSuaConta />}
          {outros.map((pendente) => (
            <div key={pendente.id}>
              Não é possível cobrar direto do aluno enquanto <strong>{pendente.nome}</strong> não
              configurar o código de beneficiário e o número de conta. Só {pendente.nome} pode fazer
              essa configuração.
            </div>
          ))}
        </div>
      }
    />
  )
}

function PendenciaDaSuaConta() {
  const estaEmbutido = useEstaEmbutido()

  return (
    <div>
      Você escolheu receber pela Efí. Configure seu código de beneficiário e número de conta — ou
      troque o banco para Hyperpay/Pilotar, que dispensa essa configuração — em{' '}
      {estaEmbutido ? (
        'Configurações, no painel da New Driver.'
      ) : (
        <a href="/configuracoes" className="underline">
          Configurações →
        </a>
      )}
    </div>
  )
}

// Only two numbers matter to the parceiro: what the student is charged, and what the parceiro keeps.
// Who else is paid out of the difference (New Driver, the pai of a captador, institutional repasses)
// is deliberately NOT itemized here — it is not the parceiro's business to see, and with a captador
// the list would expose the chain above it.
function LinkBillingForm() {
  const valor = useMatriculaStore((s) => s.valor)
  const setValor = useMatriculaStore((s) => s.setValor)
  const captadorId = useMatriculaStore((s) => s.captadorId)
  const custoTotal = useValorNewDriverTotal()

  return (
    <div>
      <div className="flex gap-3">
        <div className="flex-1" />
        <Form.Item
          label="Valor total a cobrar do aluno"
          className="!mb-0 flex-1"
          extra={`Mínimo: ${formatCurrency(custoTotal)}`}
        >
          <div className="flex justify-end">
            <CurrencyInput value={valor} onChange={setValor} />
          </div>
        </Form.Item>
      </div>
      {captadorId == null ? <SeuRepasse /> : <DetalhamentoDoRepasse />}
    </div>
  )
}

function SeuRepasse() {
  const valor = useMatriculaStore((s) => s.valor)
  const custoTotal = useValorNewDriverTotal()

  return (
    <div className="mt-6 flex flex-col gap-1 p-3 bg-green-50 rounded-lg">
      <span className="text-xs text-gray-500 uppercase tracking-wide">Seu repasse</span>
      <span className="text-xl font-semibold text-green-700">
        {formatCurrency(Math.max(0, valor - custoTotal))}
      </span>
    </div>
  )
}

// Numa venda de captador o repasse do parceiro logado é o markup dele, que fica DENTRO do piso do
// captador — invisível no "valor − custo", que ali é a margem do captador.
function DetalhamentoDoRepasse() {
  const valor = useMatriculaStore((s) => s.valor)
  const custoTotal = useValorNewDriverTotal()
  const parceiro = useAuthStore((s) => s.parceiro)
  const linhas = useRepasseAgregado()

  const margemDoCaptador = Math.max(0, valor - custoTotal)

  return (
    <div className="mt-6 rounded-lg border border-green-200 bg-green-50 p-3">
      <div className="space-y-1 text-sm">
        {linhas.map((linha) => (
          <div key={linha.descricao} className="flex items-center justify-between">
            <span>
              {linha.descricao === parceiro?.nome ? `${linha.descricao} (você)` : linha.descricao}
            </span>
            <span>{formatCurrency(linha.valor)}</span>
          </div>
        ))}
        <div className="flex items-center justify-between">
          <span>Margem do captador</span>
          <span>{formatCurrency(margemDoCaptador)}</span>
        </div>
      </div>
      <Divider className="!my-2" />
      <div className="flex items-center justify-between">
        <Text type="secondary">Total do aluno</Text>
        <Text strong>{formatCurrency(valor)}</Text>
      </div>
    </div>
  )
}

// O backend devolve repasseExibicao por curso, já na perspectiva do parceiro logado — o pai, numa
// venda de captador.
function useRepasseAgregado(): Array<{ descricao: string; valor: number }> {
  const cursoSelecionado = useMatriculaStore((s) => s.cursoSelecionado)
  const cursosAdicionais = useMatriculaStore((s) => s.cursosAdicionais)
  const valoresPorCurso = useMatriculaStore((s) => s.valoresPorCurso)

  const totalPorDescricao = new Map<string, number>()
  for (const id of [cursoSelecionado, ...cursosAdicionais]) {
    if (id == null) continue
    for (const linha of valoresPorCurso[id]?.repasseExibicao ?? []) {
      totalPorDescricao.set(
        linha.descricao,
        (totalPorDescricao.get(linha.descricao) ?? 0) + linha.valor
      )
    }
  }
  return [...totalPorDescricao].map(([descricao, valor]) => ({ descricao, valor }))
}

function ValidationStatus() {
  const cursosAdicionais = useMatriculaStore((s) => s.cursosAdicionais)
  if (cursosAdicionais.some((id) => id != null)) return <PerCourseValidationStatus />
  return <SingleCourseValidationStatus />
}

// One line per course: each is validated independently, so the parceiro sees exactly WHICH course
// the student was barred from and why, instead of a single verdict for the whole bundle.
function PerCourseValidationStatus() {
  const validacaoPorCurso = useMatriculaStore((s) => s.validacaoPorCurso)
  const cursoSelecionado = useMatriculaStore((s) => s.cursoSelecionado)
  const cursosAdicionais = useMatriculaStore((s) => s.cursosAdicionais)
  const allCursos = useMatriculaStore((s) => s.allCursos)

  const ids = [cursoSelecionado, ...cursosAdicionais].filter((id): id is number => id != null)
  const comResultado = ids.filter((id) => validacaoPorCurso[id])
  if (!comResultado.length) return null

  const nomePorId = new Map(allCursos.map((curso) => [curso.id, curso.nomeReduzido || curso.nome]))

  return (
    <div className="flex flex-col gap-4">
      {comResultado.map((id, idx) => {
        const validacao = validacaoPorCurso[id]
        const nome = nomePorId.get(id) ?? `Curso ${id}`
        return (
          <Alert
            key={id}
            showIcon
            type={validacao.loading ? 'info' : validacao.valido ? 'success' : 'error'}
            icon={
              validacao.loading ? (
                <Spin size="small" />
              ) : validacao.valido ? (
                <CheckCircleOutlined />
              ) : (
                <CloseCircleOutlined />
              )
            }
            description={
              <span className="flex items-center gap-2">
                <SlotBadge n={idx + 1} />
                <strong>{nome}</strong>
                <span>
                  {validacao.loading
                    ? 'Validando...'
                    : validacao.valido
                      ? 'Aluno apto a matricular'
                      : validacao.mensagemErro || 'Aluno não apto para matrícula.'}
                </span>
              </span>
            }
          />
        )
      })}
    </div>
  )
}

function SingleCourseValidationStatus() {
  const validacao = useMatriculaStore((s) => s.validacaoAluno)

  if (!validacao.executou && !validacao.loading) return null
  if (validacao.loading) {
    return (
      <Alert description="Validando aluno..." type="info" icon={<Spin size="small" />} showIcon />
    )
  }
  if (validacao.valido) {
    return (
      <Alert
        description="Aluno apto a matricular"
        type="success"
        icon={<CheckCircleOutlined />}
        showIcon
      />
    )
  }
  return (
    <Alert
      description={validacao.mensagemErro || 'Aluno não apto para matrícula.'}
      type="error"
      icon={<CloseCircleOutlined />}
      showIcon
    />
  )
}

function SubmitButton() {
  const { isSubmitting } = useFormikContext()
  const loading = useMatriculaStore((s) => s.loading)
  const cursoSelecionado = useMatriculaStore((s) => s.cursoSelecionado)
  const cursosSemPreco = useCursosSemPreco()

  return (
    <Button
      type="primary"
      htmlType="submit"
      size="large"
      block
      loading={loading || isSubmitting}
      disabled={!cursoSelecionado || cursosSemPreco.length > 0}
      className="mt-4!"
    >
      Matricular
    </Button>
  )
}

function AggregatedSuccessScreen() {
  const cobranca = useMatriculaStore((s) => s.cobrancaAgregada)!
  const resetForm = useMatriculaStore((s) => s.resetForm)

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <Alert
        description="Matrículas criadas com sucesso! Pague a cobrança agregada abaixo."
        type="success"
        icon={<CheckCircleOutlined />}
        showIcon
      />

      <Divider />

      <div className="flex flex-col items-center gap-4">
        <Text strong className="text-lg">
          Total: {formatCurrency(cobranca.valor)}
        </Text>
        <QRCode value={cobranca.infoPix.pix} size={200} />
        <CopyableRow label="Copia e cola PIX" value={cobranca.infoPix.pix} />
      </div>

      <Alert
        type="warning"
        showIcon
        message="Pague esta cobrança agregada. Pagar qualquer matrícula individualmente cancela a cobrança agregada."
      />

      <Divider />

      <Button type="default" size="large" block onClick={resetForm}>
        Nova matrícula
      </Button>
    </div>
  )
}

function CopyableRow({ label, value }: { label: string; value: string }) {
  function handleCopy() {
    copyToClipboard(value)
    toast.success('Copiado!')
  }

  return (
    <div className="flex items-center gap-2">
      <Text strong className="shrink-0">
        {label}:
      </Text>
      <Input value={value} readOnly size="small" className="flex-1" />
      <Button icon={<CopyOutlined />} onClick={handleCopy} size="small">
        Copiar
      </Button>
    </div>
  )
}
